/* eslint-disable prettier/prettier */
import React, { useCallback, useContext } from 'react';
import classNames from 'classnames';
import { useSimulatorRoutes } from 'hooks';
import moment from 'moment';
import {
  CleanScreenResultType,
  UrineDrugScreenDto,
} from '@peartherapeutics/hylia-api-client';
import pearClient from 'apis/pearClient';
import getHyliaApiClient from 'clients/hylia-api';
import ErrorInterceptor from 'components/common/ErrorInterceptor';
import { DATE_FORMATS, unshiftDate } from 'utils/dateTime';
import { DashboardContext } from '../../context';
import WidgetWrapper from '../Widgets/WidgetWrapper';
import { CellHeader, Cell, Row, Heading } from '../Widgets/TableComponents';
import {
  BlockTop,
  BlockMiddleEmpty,
  BlockMiddleTable,
  BlockBottom,
} from '../Widgets/Blocks';
import { VerticalScrollbar } from '../Widgets/CustomScrollbars';
import ButtonShowModal from './ButtonShowModal';
import styles from './styles.module.scss';

export interface IWidgetUrineDrugScreenProps {
  subtitle: string;
  urineDrugScreens: UrineDrugScreenDto[];
  hasActivePrescription: boolean;
}

const WidgetUrineDrugScreen = (props: IWidgetUrineDrugScreenProps) => {
  const { subtitle, urineDrugScreens, hasActivePrescription } = props;
  const routes = useSimulatorRoutes();
  const {
    setOverview,
    setUsageReport,
    patientId,
    selectedAccessRight,
    treatmentSummary,
  } = useContext(DashboardContext);
  const { lastClinicVisit } = treatmentSummary!;
  const { startDate, timezone } = selectedAccessRight!;
  const udsSinceLastClinicVisit = urineDrugScreens.filter((uds) =>
    moment(uds.date)
      .tz(timezone)
      .isSameOrAfter(moment(lastClinicVisit).tz(timezone), 'date'),
  );
  const udsLast30Days = urineDrugScreens.filter((uds) =>
    moment(uds.date)
      .tz(timezone)
      .isSameOrAfter(moment().tz(timezone).subtract(30, 'days'), 'date'),
  );
  const udsToRender = lastClinicVisit ? udsSinceLastClinicVisit : udsLast30Days;

  /**
   * Sorts uds by date.
   * @param a A urine drug screen
   * @param b A urine drug screen
   * @return a number
   */
  function byDate(a: UrineDrugScreenDto, b: UrineDrugScreenDto) {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  }

  const addCleanScreenStatus = useCallback(
    async ({
      date: selectedDate,
      type,
    }: {
      date: string;
      type: CleanScreenResultType;
    }) => {
      try {
        if (patientId) {
          const { startDate, endDate, timezone } = selectedAccessRight!;
          const adjustedDate = unshiftDate(new Date(selectedDate), timezone);
          const selected = moment(adjustedDate).tz(timezone);
          const arStart = moment(startDate).tz(timezone);
          const arEnd = moment(endDate).tz(timezone);
          const today = moment().tz(timezone);
          let date = selected.toISOString();
          if (selected.isSame(arStart, 'date')) {
            date = arStart.toISOString();
          } else if (selected.isSame(arEnd, 'date')) {
            date = arEnd.toISOString();
          } else if (selected.isSame(today, 'date')) {
            date = today.toISOString();
          }
          // Call update clean screen endpoint.
          const api = await getHyliaApiClient();
          await api.createPatientCleanScreenCompliance(
            patientId,
            selectedAccessRight!.id,
            { date, type },
          );

          // Track event.
          pearClient.v2.tracking.trackUpdatedCleanScreenEvent(
            patientId,
            moment(adjustedDate).format(DATE_FORMATS.UTC_DATE),
            type,
          );

          // Update context.
          setOverview((overview) => {
            return {
              ...overview!,
              urineDrugScreens: [
                ...overview!.urineDrugScreens,
                {
                  date,
                  result: type,
                },
              ],
            };
          });
          const message =
            type === 'Negative'
              ? 'Tox screen was Completed.'
              : 'Tox screen was Not Completed.';
          ErrorInterceptor.success(message, true);
          // ErrorInterceptor.success(`Tox screen was ${type}.`, true);
        }
      } catch (e) {
        ErrorInterceptor.error(e);
      }
      // Clear the usageReport so it can be refetched for the calendar
      setUsageReport(undefined);
    },
    [patientId, selectedAccessRight, setOverview, setUsageReport],
  );

  return (
    <WidgetWrapper
      testId="widget-urine-drug-screen"
      ariaLabel="Urine Drug Screen Widget"
    >
      <BlockTop
        title={`${udsToRender.length}`}
        subtitle={subtitle}
        customComponent={
          hasActivePrescription ? (
            <ButtonShowModal
              urineDrugScreens={urineDrugScreens}
              addCleanScreenStatus={addCleanScreenStatus}
              arStartDate={startDate}
              arTimezone={timezone}
              data-testid="add-uds-button"
            />
          ) : null
        }
        data-testid="widget-urine-drug-screen-header"
      />

      {udsToRender.length ? (
        <div data-testid="presented-widget-urine-drug-screen">
          <BlockMiddleTable>
            <Heading>
              <Row>
                <CellHeader externalClasses={styles.date}>Date</CellHeader>
                <CellHeader>Result</CellHeader>
              </Row>
            </Heading>
            <VerticalScrollbar
              listLength={udsToRender.length}
              maximumItemsToHideScrollbar={3}
              universal
            >
              {udsToRender.sort(byDate).map((urineDrugScreen) => {
                const isNegative = urineDrugScreen.result === 'Negative';
                const displayText = isNegative ? 'Completed' : 'Not Completed';
                return (
                  <Row
                    key={`urine-drug-screens-${urineDrugScreen.date}`}
                    data-testid="widget-uds-row"
                  >
                    <Cell
                      externalClasses={styles.date}
                      data-testid="widget-uds-row-date"
                    >
                      {moment(urineDrugScreen.date).tz(timezone).format('l')}
                    </Cell>
                    <Cell>
                      <div
                        data-testid={`uds-status-${urineDrugScreen.result.toLowerCase()}`}
                        className={classNames(
                          styles.status,
                          styles[urineDrugScreen.result.toLowerCase()],
                        )}
                      >
                        {displayText}
                      </div>
                    </Cell>
                  </Row>
                );
              })}
            </VerticalScrollbar>
          </BlockMiddleTable>
        </div>
      ) : (
        // eslint-disable-next-line max-len
        <BlockMiddleEmpty
          content="UDS results will be displayed here. Patients earn a reward for each completed screen."
          data-testid="no-uds-results-message"
        />
      )}
      <BlockBottom
        link={routes.PATIENT_DASHBOARD_CALENDAR}
        text="Go To Calendar"
        data-testid="widget-urine-drug-screen-footer"
      />
    </WidgetWrapper>
  );
};

export default WidgetUrineDrugScreen;
